'use client';

import { proxy, useSnapshot } from 'valtio';

import { fetchHydratedMyWatches } from '@/lib/myWatches/client';
import type { HydratedWatch } from '@/lib/myWatchesData/orchestrator';
import { devError } from '@/lib/utils/devLog';

interface RegisteredWatchesCacheState {
  watches: HydratedWatch[];
  isLoading: boolean;
  error: 'fetch_failed' | 'unauthenticated' | null;
}

export const registeredWatchesCache = proxy<RegisteredWatchesCacheState>({
  watches: [],
  isLoading: false,
  error: null,
});

async function loadHydratedWatches(locale: string): Promise<void> {
  registeredWatchesCache.isLoading = true;
  registeredWatchesCache.error = null;

  try {
    const result = await fetchHydratedMyWatches(locale);

    if (result.status === 'success') {
      registeredWatchesCache.watches = result.watches;
      return;
    }

    if (result.status === 'unauthenticated') {
      registeredWatchesCache.watches = [];
      registeredWatchesCache.error = 'unauthenticated';
      return;
    }

    registeredWatchesCache.watches = [];
    registeredWatchesCache.error = 'fetch_failed';
    devError('[RegisteredWatchesCache] Hydrated fetch failed');
  } catch (error) {
    registeredWatchesCache.watches = [];
    registeredWatchesCache.error = 'fetch_failed';
    devError('[RegisteredWatchesCache] Hydrated fetch error:', error);
  } finally {
    registeredWatchesCache.isLoading = false;
  }
}

export const registeredWatchesActions = {
  fetch(locale: string) {
    if (registeredWatchesCache.isLoading) {
      return Promise.resolve();
    }

    return loadHydratedWatches(locale);
  },
  refetch(locale: string) {
    return loadHydratedWatches(locale);
  },
};

export function useRegisteredWatchesCache() {
  return useSnapshot(registeredWatchesCache);
}
